import { useContext, useState } from "react";
import { Box, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useTranslation } from "react-i18next";
import { InfoCard, CustomButton, SnackBar } from "src/design";
import { UserContext } from "src/Utils/Context/UserContext";
import { useSendPasswordResetEmail } from "src/data/account/owner";
import { colorTextPrimary, colorTextMuted } from "src/design/tokens";

export const PasswordSecurityCard = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const { refetch: sendPasswordResetEmail } = useSendPasswordResetEmail(
    user?.email,
  );

  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const handleSendReset = async () => {
    try {
      await sendPasswordResetEmail();
      setToast({
        open: true,
        message: t("configuration.password.resetSuccess"),
        severity: "success",
      });
    } catch {
      setToast({
        open: true,
        message: t("configuration.password.resetError"),
        severity: "error",
      });
    }
  };

  return (
    <>
      <InfoCard
        title={t("configuration.password.title")}
        subtitle={t("configuration.password.subtitle")}
        icon={<LockOutlinedIcon />}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, color: colorTextPrimary }}>
              {t("configuration.password.label")}
            </Typography>
            <Typography variant="body2" sx={{ color: colorTextMuted }}>
              {t("configuration.password.resetHint", {
                email: user?.email ?? "",
              })}
            </Typography>
          </Box>
          <CustomButton
            color="inverted"
            startIcon={<MailOutlineIcon />}
            onClick={handleSendReset}
          >
            {t("configuration.password.sendReset")}
          </CustomButton>
        </Box>
      </InfoCard>

      <SnackBar
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};
